import React, { useEffect, useState } from "react";
import { LuX } from "react-icons/lu";
import { Button, ConfirmDialog, Input, notify, SkeletonRows, Spinner } from "../../../components/ui";
import { cn } from "../../../components/ui/cn";
import { AddLink, ITEM_CLASS, ROW_ICON_BUTTON } from "./LinkedList";
import { useSecretaries } from "../useSecretaries";
import { t } from "../../../i18n";
import { emailError } from "../../../lib/validators";

const MAX_EMAILS = 2;

// E-mails de secretárias com acesso à conta do médico (até 2).
export const SecretaryEmails: React.FC = () => {
  const [emails, setEmails] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<string | null>(null);
  const [deletingEmail, setDeletingEmail] = useState<string | null>(null);
  const [listed, setListed] = useState(false);
  const { addSecretaries, deleteSecretary, listSecretaries, loading } = useSecretaries();

  useEffect(() => {
    listSecretaries().then((result) => {
      if (result) setEmails(result.emails);
      setListed(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAdd = async () => {
    const email = input.trim().toLowerCase();
    if (emailError(email)) return notify({ tone: "error", title: t("settings.secretaries.invalid") });
    if (emails.includes(email)) return notify({ tone: "error", title: t("settings.secretaries.duplicate") });
    if (emails.length >= MAX_EMAILS) return notify({ tone: "error", title: t("settings.secretaries.max", { max: MAX_EMAILS }) });

    const result = await addSecretaries([email]);
    if (!result) return;
    setEmails(result.saved_emails);
    setInput("");
  };

  const handleConfirmDelete = async () => {
    if (!emailToDelete) return;
    const email = emailToDelete;
    setEmailToDelete(null);
    setDeletingEmail(email);
    const result = await deleteSecretary(email);
    setDeletingEmail(null);
    if (result) setEmails((prev) => prev.filter((e) => e !== email));
  };

  const limitReached = emails.length >= MAX_EMAILS;
  const adding = loading && !deletingEmail;
  const countKey = emails.length === 1 ? "settings.secretaries.countOne" : "settings.secretaries.countOther";

  if (!listed) return <SkeletonRows label={t("settings.secretaries.loading")} rows={1} />;

  return (
    <div className="flex flex-col gap-3">
      {emailToDelete && (
        <ConfirmDialog
          title={t("settings.secretaries.removeTitle")}
          message={t("settings.secretaries.removeConfirm", { email: emailToDelete })}
          confirmLabel={t("settings.secretaries.removeAction")}
          onConfirm={handleConfirmDelete}
          onCancel={() => setEmailToDelete(null)}
        />
      )}

      {emails.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {emails.map((email) => {
            const deleting = deletingEmail === email;
            return (
              <li key={email} className={cn(ITEM_CLASS, deleting && "opacity-50")}>
                {deleting && <Spinner size={14} />}
                <span className={cn("min-w-0 flex-1 break-words text-body", deleting ? "text-fg-subtle" : "text-fg")}>{email}</span>
                <button
                  type="button"
                  onClick={() => setEmailToDelete(email)}
                  disabled={!!deletingEmail || loading}
                  aria-label={t("settings.secretaries.remove", { email })}
                  className={cn(ROW_ICON_BUTTON, "hover:bg-danger-soft hover:text-danger")}
                >
                  <LuX size={16} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {open ? (
        <div className="flex gap-2">
          <Input
            type="email"
            aria-label={t("settings.secretaries.label")}
            placeholder={limitReached ? t("settings.secretaries.limitReached") : t("settings.secretaries.placeholder")}
            value={input}
            disabled={limitReached || loading}
            autoFocus
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              handleAdd();
            }}
          />
          <Button variant="primary" onClick={handleAdd} loading={adding} disabled={limitReached || !input.trim() || loading}>
            {t("settings.secretaries.add")}
          </Button>
        </div>
      ) : (
        !limitReached && <AddLink onClick={() => setOpen(true)}>{t("settings.secretaries.addLink")}</AddLink>
      )}

      <p className="text-caption text-fg-subtle">{t(countKey, { count: emails.length, max: MAX_EMAILS })}</p>
    </div>
  );
};
